import { AxiosStatic } from "axios";

import { PartInstanceSummary } from "../../App/Analytics/Common/types";
import { AxiosInterceptedResult } from "../interceptors";

export interface IAnalyticsApiDependencies {
  axios: AxiosStatic;
  saltcomplianceApiRoot: string;
}

export interface IAnalyticsApi {
  getClassSummarySet: (accountId: string, courseId: string, classId: string) => Promise<AxiosInterceptedResult<any>>;
  getClassCourseInstancesSummaries: (
    accountId: string,
    courseId: string,
    classId: string,
    setId: number
  ) => Promise<AxiosInterceptedResult<any>>;
  getClassCompactUnitSummaries: (
    accountId: string,
    courseId: string,
    classId: string,
    setId: number
  ) => Promise<AxiosInterceptedResult<any>>;
  getClassUnitRollup: (
    accountId: string,
    courseId: string,
    classId: string,
    setId: number,
    unitIndex: string
  ) => Promise<AxiosInterceptedResult<any>>;
  getClassPartInstSummaryRollup: (
    accountId: string,
    courseId: string,
    classId: string,
    setId: number
  ) => (partPosition: string) => Promise<AxiosInterceptedResult<PartInstanceSummary[]>>;
  getScormPackageSummarySet: (
    accountId: string,
    dummyCourseId: string,
    scormPackageId: string
  ) => Promise<AxiosInterceptedResult<any>>;
  getScormPackageCourseInstancesSummaries: (
    accountId: string,
    dummyCourseId: string,
    scormPackageId: string,
    setId: number
  ) => Promise<AxiosInterceptedResult<any>>;
  getScormPackageCompactUnitSummaries: (
    accountId: string,
    dummyCourseId: string,
    scormPackageId: string,
    setId: number
  ) => Promise<AxiosInterceptedResult<any>>;
  getScormPackageUnitRollup: (
    accountId: string,
    scormPackageId: string,
    setId: number,
    unitIndex: string
  ) => Promise<AxiosInterceptedResult<any>>;
  getScormPackagePartInstSummaryRollup: (
    accountId: string,
    scormPackageId: string,
    setId: number
  ) => (partPosition: string) => Promise<AxiosInterceptedResult<PartInstanceSummary[]>>;
}

export default class AnalyticsApi implements IAnalyticsApi {
  private readonly apiRoot: string;
  private readonly axios: AxiosStatic;

  constructor(dependencies: IAnalyticsApiDependencies) {
    this.apiRoot = dependencies.saltcomplianceApiRoot;
    this.axios = dependencies.axios;
  }

  public getClassSummarySet = (accountId: string, courseId: string, classId: string) => {
    return (this.axios.get(`${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/set`, {
      params: { activeUser: accountId },
    }) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getClassCourseInstancesSummaries = (accountId: string, courseId: string, classId: string, setId: number) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/set/${setId}/courseInstSummaries/rollup`,
      {
        params: { activeUser: accountId },
      }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getClassCompactUnitSummaries = (accountId: string, courseId: string, classId: string, setId: number) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/set/${setId}/unitInstSummariesV3/cc.root/rollup`,
      {
        params: { activeUser: accountId },
      }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getClassPartInstSummaryRollup = (accountId: string, courseId: string, classId: string, setId: number) => (
    partPosition: string
  ): Promise<AxiosInterceptedResult<PartInstanceSummary[]>> =>
    (this.axios.get(
      `${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/set/${setId}/partInstSummaries/cc.root.${partPosition}/rollup`,
      { params: { activeUser: accountId } }
    ) as any) as Promise<AxiosInterceptedResult<PartInstanceSummary[]>>;

  public getClassUnitRollup = (
    accountId: string,
    courseId: string,
    classId: string,
    setId: number,
    unitIndex: string
  ) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byCourse/${courseId}/byClass/${classId}/set/${setId}/unitInstSummariesV3/cc.root.${unitIndex}/rollup`,
      { params: { activeUser: accountId } }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  // NOTE: The purpose of dummyCourseId in the following functions is to match
  // the function signature of getClass* methods to avoid some code repetition
  // in the saga 'fetchCourseCompletionAnalytics'.
  public getScormPackageSummarySet = (accountId: string, dummyCourseId: string, scormPackageId: string) => {
    return (this.axios.get(`${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/set`, {
      params: { activeUser: accountId },
    }) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getScormPackageCourseInstancesSummaries = (
    accountId: string,
    dummyCourseId: string,
    scormPackageId: string,
    setId: number
  ) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/set/${setId}/courseInstSummaries/rollup`,
      {
        params: { activeUser: accountId },
      }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getScormPackageCompactUnitSummaries = (
    accountId: string,
    dummyCourseId: string,
    scormPackageId: string,
    setId: number
  ) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/set/${setId}/unitInstSummariesV3/cc.root/rollup`,
      {
        params: { activeUser: accountId },
      }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getScormPackageUnitRollup = (accountId: string, scormPackageId: string, setId: number, unitIndex: string) => {
    return (this.axios.get(
      `${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/set/${setId}/unitInstSummariesV3/cc.root.${unitIndex}/rollup`,
      { params: { activeUser: accountId } }
    ) as any) as Promise<AxiosInterceptedResult<any>>;
  };

  public getScormPackagePartInstSummaryRollup = (accountId: string, scormPackageId: string, setId: number) => (
    partPosition: string
  ): Promise<AxiosInterceptedResult<PartInstanceSummary[]>> =>
    (this.axios.get(
      `${this.apiRoot}/analytics/byScormPackage/${scormPackageId}/set/${setId}/partInstSummaries/cc.root.${partPosition}/rollup`,
      { params: { activeUser: accountId } }
    ) as any) as Promise<AxiosInterceptedResult<PartInstanceSummary[]>>;
}
